/*
 * Copyright (c) Nexign, JSC, 2021-2023.
 */
/**
 * Hook for table footer buttons configuration
 *
 * @author Yakov Gushchin
 * @author Ilya Babanov
 * @author Alexander Golshtein
 * @author Evgeniy Naumov
 * @version 1.25.0
 * @since 1.12.0
 */

import { useCallback } from 'react';

import find from 'lodash/find';
import { useDispatch, useSelector } from 'react-redux';

import { startTick } from '@components/Table/actions/tableActions';
import { ITableReducer } from '@components/Table/reducers/tableReducer';
import { makeSelectorTableButtons } from '@components/Table/selectors/tableSelectors';
import { mapTableFooterToTableName, TableFooterNameType, TableNameType } from '@components/Table/types/tableTypes';
import { makeSelectTranslationCommon } from '@modules/internationalization/intSelectors';
import { ICommonTranslation } from '@modules/internationalization/types';
import { ELogBusinessEventName, ELogBusinessEventType } from '@modules/logging';
import { logBusinessEvent as logBusinessEventAction } from '@modules/logging/loggingActions';
import { EOnboardingComponentsId } from '@modules/onboarding/types/onboardingTypes';
import { AccessComponentKeyEnum } from '@modules/role/constants/guard/guardConstants';
import { setSessionStorageData } from '@modules/storage/storageActions';

import { ITableButtonTypes } from '../types';
import useTableFooterButtonAccessLevel from './buttons/useTableFooterButtonAccessLevel';
import useTableFooterButtonAdditionalAccount from './buttons/useTableFooterButtonAdditionalAccount';
import useTableFooterButtonBills from './buttons/useTableFooterButtonBills';
import useTableFooterButtonBillsCompare from './buttons/useTableFooterButtonBillsCompare';
import useTableFooterButtonCallDetails from './buttons/useTableFooterButtonCallDetails';
import useTableFooterButtonCorporate from './buttons/useTableFooterButtonCorporate';
import useTableFooterButtonDcRules from './buttons/useTableFooterButtonDcRules';
import useTableFooterButtonExpenses from './buttons/useTableFooterButtonExpenses';
import useTableFooterButtonInquiryCreate from './buttons/useTableFooterButtonInquiryCreate';
import useTableFooterButtonLabelEdit from './buttons/useTableFooterButtonLabelEdit';
import useTableFooterButtonLimit from './buttons/useTableFooterButtonLimit';
import useTableFooterButtonManageBlock from './buttons/useTableFooterButtonManageBlock';
import useTableFooterButtonMobileCash from './buttons/useTableFooterButtonMobileCash';
import useTableFooterButtonMobileReport from './buttons/useTableFooterButtonMobileReport';
import useTableFooterButtonPayment from './buttons/useTableFooterButtonPayment';
import useTableFooterButtonReconciliation from './buttons/useTableFooterButtonReconciliation';
import useTableFooterButtonShowDailyExpenses from './buttons/useTableFooterButtonShowDailyExpenses';
import useTableFooterButtonShowOnGraph from './buttons/useTableFooterButtonShowOnGraph';
import useTableFooterButtonShowSubsExpenses from './buttons/useTableFooterButtonShowSubsExpenses';
import useTableFooterButtonTariffChange from './buttons/useTableFooterButtonTariffChange';
import useTableFooterButtonUpdateBalance from './buttons/useTableFooterButtonUpdateBalance';
import useTableFooterButtonWizardBillsDoc from './buttons/useTableFooterButtonWizardBillsDoc';
import useTableFooterButtonWizardExpensesDetail from './buttons/useTableFooterButtonWizardExpensesDetail';
import useTableFooterButtonWizardMoneyTransfer from './buttons/useTableFooterButtonWizardMoneyTransfer';

export interface IUseTableFooterButtons {
    className: string;
    dataName: string;
    icon: string;
    id: AccessComponentKeyEnum;
    onboarding?: EOnboardingComponentsId;
    fastInquiryCreateTitle: string;
    text: string;
    tooltipMsg: string;

    onClick: (...args: any) => void;
}

export type TUseTableFooterButtonsUrl = {
    [key in TableFooterNameType]: {
        external: string;
        url: string;
    }
}

export type TUseTableFooterButtonsOnboarding = {
    [key in TableFooterNameType]?: {
        onboarding: EOnboardingComponentsId;
    }
}

export type TTableButtonsLogBusinessEvent = (eventName: ELogBusinessEventName, type: ELogBusinessEventType) => void;
export type TTableButtonsGetTranslation = (id: AccessComponentKeyEnum) => Pick<IUseTableFooterButtons, 'fastInquiryCreateTitle' | 'text' | 'tooltipMsg'>
export type TTableButtonsUpdateTimer = (id: AccessComponentKeyEnum) => void;

/* TODO: Перенести в module/table */
const useTableFooterButtons = (
    tableFooterName: TableFooterNameType,
    buttonsProps?: ITableButtonTypes[],
    id?: string,
) => {
    const dispatch = useDispatch();

    /* TODO: remove TableNameType */
    const getTableName = useCallback(() =>
        mapTableFooterToTableName[tableFooterName] as TableNameType, [tableFooterName]);

    const translation: ICommonTranslation = useSelector(makeSelectTranslationCommon());

    /* TODO: remove buttonsFromStore, use only buttons */
    const buttonsFromStore: ITableReducer[TableNameType]['buttons'] = useSelector(makeSelectorTableButtons(getTableName()));
    const buttons = buttonsProps || buttonsFromStore;

    const log = useCallback((eventName: ELogBusinessEventName, type: ELogBusinessEventType) => {
        dispatch(logBusinessEventAction({ eventName, type }));
    }, [dispatch]);

    const getTranslation = useCallback(
        (id: AccessComponentKeyEnum): Pick<IUseTableFooterButtons, 'fastInquiryCreateTitle' | 'text' | 'tooltipMsg'> => {
            /* TODO: remove TableNameType */
            const tableName = mapTableFooterToTableName[tableFooterName] as TableNameType;
            return ({
                fastInquiryCreateTitle: translation.corporateTableFooter.types[tableName]?.[id]?.fastInquiryCreateTitle || '',
                text: translation.corporateTableFooter.types[tableName]?.[id]?.title || '',
                tooltipMsg: translation.corporateTableFooter.types[tableName]?.[id]?.tooltip || '',
            });
        }, [tableFooterName, translation.corporateTableFooter]);

    const updateTimer = useCallback((id: AccessComponentKeyEnum) => {
        const buttonMeta: ITableButtonTypes | undefined = find(buttons, { buttonName: id });
        if (buttonMeta && buttonMeta.lockTime) {
            dispatch(setSessionStorageData({
                key: 'timers', value: [{
                    buttonName: buttonMeta.buttonName,
                    time: buttonMeta.lockTime * 1000 * 60,
                }],
            }));
            dispatch(startTick());
        }
    }, [dispatch, buttons]);

    return {
        // Уровень доступа
        accessLevel: useTableFooterButtonAccessLevel(getTranslation, log),
        // Управлять доп.счетами
        additionalAccount: useTableFooterButtonAdditionalAccount(getTranslation, tableFooterName),
        // Получить документы
        bills: useTableFooterButtonBills(getTranslation, log, tableFooterName),
        // Сравнить счета
        billsCompare: useTableFooterButtonBillsCompare(getTranslation, tableFooterName),
        // Заказать детализацию
        callDetails: useTableFooterButtonCallDetails(getTranslation, log, tableFooterName),
        // Услуги и опции
        corporate: useTableFooterButtonCorporate(getTranslation),
        // Разделение расходов
        dcRules: useTableFooterButtonDcRules(getTranslation, tableFooterName),
        // Посмотреть расходы
        expenses: useTableFooterButtonExpenses(getTranslation, log, tableFooterName),
        // Создать обращение
        inquiryCreate: useTableFooterButtonInquiryCreate(getTranslation, log, tableFooterName, id),
        // Изменить метки
        labelEdit: useTableFooterButtonLabelEdit(getTranslation, tableFooterName),
        // Настроить лимиты
        limit: useTableFooterButtonLimit(getTranslation, tableFooterName),
        // Управление блокировкой
        manageBlock: useTableFooterButtonManageBlock(getTranslation, log, tableFooterName),
        // Выставить счет
        mobileCash: useTableFooterButtonMobileCash(getTranslation),
        // Сформировать отчет
        mobileReport: useTableFooterButtonMobileReport(getTranslation, log, tableFooterName),
        // Посмотреть платежи
        payment: useTableFooterButtonPayment(getTranslation, log),
        // Заказать акт сверки
        reconciliation: useTableFooterButtonReconciliation(getTranslation, log, tableFooterName),
        // Расходы по дням
        showDailyExpenses: useTableFooterButtonShowDailyExpenses(getTranslation),
        // Показать на графике
        showOnGraph: useTableFooterButtonShowOnGraph(getTranslation),
        // Расходы абонентов
        showSubsExpenses: useTableFooterButtonShowSubsExpenses(getTranslation),
        // Изменить тариф
        tariffChange: useTableFooterButtonTariffChange(getTranslation),
        // Обновить баланс
        updateBalance: useTableFooterButtonUpdateBalance(getTranslation, updateTimer),
        // Мастер заказа отчетных документов
        wizardBillsDoc: useTableFooterButtonWizardBillsDoc(getTranslation, log, tableFooterName),
        wizardExpensesDetail: useTableFooterButtonWizardExpensesDetail(getTranslation),
        // Перевести средства
        wizardMoneyTransfer: useTableFooterButtonWizardMoneyTransfer(getTranslation, tableFooterName),
    };
};

export default useTableFooterButtons;
